import { FormControl, FormControlError, FormGroup, FormLabel } from '@hai/ui-react';
import { useField } from 'formik';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { FormChangeEvent } from 'src/js/util/global-type';
import { isNilOrEmpty } from 'src/js/util/global-util';

interface Props {
  id?: string;
  name: string;
  mask?: boolean;
  title?: string;
  placeholder?: string;
  hint?: string;
  alwaysShowHint?: boolean;
  required?: boolean;
  type?: string;
  pwdToggle?: boolean;
  disabled?: boolean;
  controlProps?: any;
  autoFocus?: boolean;
  autoComplete?: string;
  className?: string;
  inputClassName?: string;
  onChange?: (event: React.KeyboardEvent<HTMLInputElement> & FormChangeEvent) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement> & FormChangeEvent) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement> & FocusEvent) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement> & FocusEvent) => void;
  inputRef?: MutableRefObject<any>;
  visibilityHidden?: boolean;
}

export const FormikTextfield: React.FunctionComponent<Props> = ({
  id,
  name,
  mask,
  title,
  placeholder,
  hint,
  alwaysShowHint,
  required,
  type = 'text',
  pwdToggle,
  disabled,
  autoFocus,
  autoComplete,
  className,
  inputClassName,
  onChange,
  onKeyUp,
  onFocus,
  onBlur,
  inputRef,
  visibilityHidden,
}) => {
  const [field, meta] = useField(name);
  const { value: value, onChange: fieldOnChange, onBlur: fieldOnBlur, ...restField } = field; // restField contains name, multiple and checked
  const hasErrors = meta.error && meta.touched;
  let additionalClassNames = className ? ` ${className}` : '';
  if (visibilityHidden) {
    additionalClassNames += ' invisible';
  }

  const [focus, setFocus] = useState(false);

  const checkIfNumber = (event: React.KeyboardEvent<HTMLInputElement> & FormChangeEvent) => {
    // console.log('checkIfNumber', event.key)
    return (
      !/(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight|Control|Meta|Home|End|v|c|x)/.test(event.key) &&
      event.preventDefault()
    );
  };

  // onWheel handle BEGINs
  const onWheel = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // console.log('onWheel', event);
    if (event && event.target && typeof (event.target as HTMLElement).blur === 'function') {
      (event.target as HTMLElement).blur();
      // the three following lines are disabled because they prevent scrolling when directly over the field
      // event.preventDefault();
      // event.stopPropagation();
      // event.nativeEvent?.stopImmediatePropagation();
    }
  };
  if (inputRef == null) {
    inputRef = useRef();
  }
  useEffect(() => {
    let cleanupNeeded = false;
    const refSaved = inputRef.current;
    if (type === 'number' && inputRef.current) {
      refSaved.addEventListener('wheel', onWheel);
      cleanupNeeded = true;
    }
    return () => {
      if (cleanupNeeded) {
        refSaved.removeEventListener('wheel', onWheel);
      }
    };
  }, [inputRef]);
  // onWheel handle ENDs

  let shuffleHint = true;
  if (alwaysShowHint) {
    shuffleHint = false;
  }

  let showHint = alwaysShowHint ? 'always' : !isNilOrEmpty(hint);

  const showError = hasErrors; // before it was: isNilOrEmpty(hint) ? true : !focus;
  if (showError) {
    showHint = false;
    additionalClassNames += ' hasError';
  }
  if (alwaysShowHint) {
    additionalClassNames += ' hintMarginFix';
  }

  return (
    <FormGroup className={`FormItem${additionalClassNames}`}>
      {title && (
        <FormLabel required={required} htmlFor={id || name}>
          {title}
        </FormLabel>
      )}
      <FormControl
        id={id || name}
        className={inputClassName}
        useFsMask={mask}
        type={type}
        pwdToggle={pwdToggle}
        placeholder={shuffleHint ? (focus ? hint ?? placeholder : placeholder ?? hint) : placeholder}
        hintText={hint}
        invalid={hasErrors}
        disabled={disabled}
        autoFocus={autoFocus}
        inputRef={inputRef}
        onKeyUp={onKeyUp}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement> & FormChangeEvent) =>
          type === 'number' && checkIfNumber(event)
        }
        onChange={(event: React.KeyboardEvent<HTMLInputElement> & FormChangeEvent): void => {
          onChange?.(event);
          fieldOnChange(event);
        }}
        onFocus={(event: React.FocusEvent<HTMLInputElement> & FocusEvent): void => {
          setFocus(true);
          onFocus?.(event);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement> & FocusEvent): void => {
          setFocus(false);
          fieldOnBlur(event);
          onBlur?.(event);
        }}
        value={value}
        autoComplete={autoComplete ?? 'off'}
        showHint={showHint}
        {...restField}
      />
      {showError && <FormControlError show={hasErrors} mssg={meta.error} />}
    </FormGroup>
  );
};
